import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import FallbackImage from '../components/FallbackImage';
import PriceDisplay from '../components/PriceDisplay';
import ProductRecommendations from '../components/ProductRecommendations';
import { useCart } from '../components/CartDrawer';
import { sampleProductsPath } from '../lib/paths';
import { getProduct, getProducts, getRecommendedProducts } from '../lib/products';

const DETAIL_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'ingredients', label: 'Ingredients' },
  { id: 'usage', label: 'How to use' },
  { id: 'concerns', label: 'Concerns' },
];

export default function ProductDetailPage() {
  const navigate = useNavigate();
  const { slug = '' } = useParams();
  const { addItem } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('overview');

  const products = useMemo(() => getProducts(), []);
  const product = useMemo(() => getProduct(slug), [slug]);
  const recommendations = useMemo(
    () => (product ? getRecommendedProducts(product, products) : []),
    [product, products]
  );

  useEffect(() => {
    document.title = product ? `${product.name} | Nyafaz Skincare` : 'Product | Nyafaz Skincare';
    setQuantity(1);
    setActiveTab('overview');
    const scroller = document.querySelector('.sample-products-shell .sp-content');
    if (scroller) scroller.scrollTop = 0;
  }, [product, slug]);

  if (!product) {
    return (
      <main className="page">
        <div className="empty">
          Product not found.
          <div style={{ marginTop: 16 }}>
            <Link to={sampleProductsPath('/products')} className="btn">
              Back to Products
            </Link>
          </div>
        </div>
      </main>
    );
  }

  const disableCheckout = !product.variant_id;

  function handleAddToCart() {
    addItem(product, quantity);
  }

  function handleBuyNow() {
    if (disableCheckout) return;
    addItem(product, quantity);
    navigate(sampleProductsPath('/checkout'));
  }

  return (
    <main className="page page--detail">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to={sampleProductsPath('/products')}>Products</Link>
        <span>/</span>
        <Link to={sampleProductsPath('/products')}>{product.category}</Link>
        <span>/</span>
        <span className="breadcrumb-current">{product.name}</span>
      </nav>

      <section className="pdp-hero">
        <div className="pdp-gallery">
          <FallbackImage src={product.imageUrl} alt={product.name} />
          <div className="pdp-gallery-meta">
            <span className="badge">{product.category}</span>
            <span className="badge badge-soft">{product.gender}</span>
          </div>
        </div>

        <aside className="pdp-buybox">
          <p className="pdp-brand">{product.brand}</p>
          <h1 className="pdp-title">{product.name}</h1>

          <div className="pdp-rating-row">
            <span className="pdp-stars" aria-hidden="true">★★★★☆</span>
            <span className="muted">4.7 · Demo rating</span>
          </div>

          <PriceDisplay mrp={product.mrp} price={product.price} discount={product.discount} />

          <p className="pdp-usp">{product.majorUsp}</p>

          <div className="pdp-chip-block">
            <p className="pdp-label">Best for</p>
            <div className="badge-row">
              {product.conditions.map((condition) => (
                <span className="badge" key={condition}>
                  {condition}
                </span>
              ))}
            </div>
          </div>

          <div className="pdp-chip-block">
            <p className="pdp-label">Routine</p>
            <div className="badge-row">
              {product.routines.map((routine) => (
                <span className="badge badge-soft" key={routine}>
                  {routine === 'AM' ? 'Morning' : 'Night'}
                </span>
              ))}
            </div>
          </div>

          <div className="pdp-qty-row">
            <p className="pdp-label">Quantity</p>
            <div className="qty">
              <button
                className="btn ghost"
                type="button"
                aria-label="Decrease quantity"
                onClick={() => setQuantity((value) => Math.max(1, value - 1))}
              >
                −
              </button>
              <span className="qty-value">{quantity}</span>
              <button
                className="btn ghost"
                type="button"
                aria-label="Increase quantity"
                onClick={() => setQuantity((value) => value + 1)}
              >
                +
              </button>
            </div>
          </div>

          <div className="actions product-cta">
            <button className="btn" type="button" onClick={handleAddToCart}>
              Add to cart
            </button>
            <button className="btn secondary" type="button" onClick={handleBuyNow} disabled={disableCheckout}>
              {disableCheckout ? 'Checkout unavailable' : 'Buy now'}
            </button>
          </div>

          <dl className="pdp-meta">
            <div>
              <dt>SKU</dt>
              <dd>{product.sku}</dd>
            </div>
            <div>
              <dt>Variant</dt>
              <dd>{product.variant_id || '—'}</dd>
            </div>
          </dl>
        </aside>
      </section>

      <section className="pdp-details">
        <div className="pdp-tabs" role="tablist" aria-label="Product details">
          {DETAIL_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`pdp-tab ${activeTab === tab.id ? 'is-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="pdp-panel section-card" role="tabpanel">
          {activeTab === 'overview' && (
            <>
              <h2>Product overview</h2>
              <p>{product.majorUsp}</p>
              <p className="muted" style={{ marginTop: 14 }}>
                {product.description}
              </p>
            </>
          )}

          {activeTab === 'ingredients' && (
            <>
              <h2>Key ingredients</h2>
              <div className="ingredient-list">
                {product.ingredients.map((ingredient) => (
                  <article className="ingredient-item" key={ingredient}>
                    <h3>{ingredient}</h3>
                    <p className="muted">Featured active in this demo formula.</p>
                  </article>
                ))}
              </div>
            </>
          )}

          {activeTab === 'usage' && (
            <>
              <h2>How to use</h2>
              <p>{product.usage}</p>
              <ol className="usage-steps">
                <li>Cleanse with a lukewarm rinse.</li>
                <li>Apply a pea-sized amount and massage gently.</li>
                <li>Follow with the rest of your {product.routines.join(' / ')} routine.</li>
              </ol>
            </>
          )}

          {activeTab === 'concerns' && (
            <>
              <h2>Skin conditions & concerns</h2>
              <div className="conditions">
                {product.conditions.map((condition) => (
                  <div className="condition" key={condition}>
                    {condition}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <ProductRecommendations products={recommendations} />
    </main>
  );
}
